import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import "./Table.css";

const ReusableTable = ({
  data = [],
  columns = [],

  mode = "client",

  page = 0,
  pageSize = 10,
  pageCount = -1,

  onPageChange = () => {},
}) => {
  const tableColumns = useMemo(() => {
    if (columns.length > 0) return columns;
    if (data.length === 0) return [];
    return Object.keys(data[0]).map((key) => ({
      accessorKey: key,
      header: key.charAt(0).toUpperCase() + key.slice(1),
    }));
  }, [columns, data]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      pagination: {
        pageIndex: page,
        pageSize: pageSize,
      },
    },
    manualPagination: mode === "server",
    pageCount: mode === "server" ? pageCount : undefined,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function"
          ? updater({ pageIndex: page, pageSize })
          : updater;
      onPageChange(next.pageIndex, next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortingRowModel: mode === "client" ? getSortedRowModel() : undefined,
    getFilteredRowModel: mode === "client" ? getFilteredRowModel() : undefined,
    getPaginationRowModel:
      mode === "client" ? getPaginationRowModel() : undefined,
  });

  return (
    <div className="table-wrapper">
      <div className="table-scroll">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="header-cell">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}

                      {header.column.getIsSorted() === "asc" && " 🔼"}
                      {header.column.getIsSorted() === "desc" && " 🔽"}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {data.length === 0 && (
          <div className="empty-state">No records found.</div>
        )}
      </div>

      <div className="pagination">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {"<"}
        </button>

        <span>
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {mode === "server" ? pageCount : table.getPageCount()}
        </span>

        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          {">"}
        </button>

        <select
          value={pageSize}
          onChange={(e) => onPageChange(page, Number(e.target.value))}
        >
          {[5, 10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default ReusableTable;
